<template>
  <form-layout
    :title="formTitle"
    :is-saving="addingJobsToBatch"
    hide-bottom-bar
    @on-dismiss="dismiss"
  >
    <vs-form>
      <vs-wrapper>
        <batch-edit-form-fields
          v-if="showEditBatchForm && selectedBatchId !== null && selectedBatchId !== undefined"
          :id="selectedBatchId"
          @batch-updated="dismiss"
        />
        <batch-draft-form
          v-else-if="showDraftForm"
          :batch-name="draftBatchName"
          :jobs-uuid-list="[jobUuid]"
          @batch-created-success="handleBatchCreatedSuccess"
          @batch-created-failure="handleBatchCreatedFailure"
        />
        <div v-else>
          <div class="mb-8">
            <batch-creation-form
              :initial-batch-number-value="currentSearchQuery"
              :add-divider="false"
              button-label="Create batch and add jobs"
              @empty-batch-number="handleEmptyBatchNumber"
              @batch-draft-requested="handleBatchDraftRequested"
            />
          </div>
          <batches-list ref="batchesListRef" :hub-id="hubIdRef" @select="onSelect" />
        </div>
      </vs-wrapper>
    </vs-form>

    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
        {{ snackbarMessage ?? '' }}
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import moment from 'moment'
import FormLayout from '@/components/common/FormLayout.vue'
import BatchesList from '@/components/batches/BatchesList.vue'
import { useAddJobsToInvoiceMutation, useCreateInvoiceMutation } from '@/api/invoice'
import { computed, ref } from 'vue'
import BatchCreationForm from '@/components/batches/BatchCreationForm.vue'
import { AxiosError } from 'axios'
import { SnackType } from '@/components/vision/SnackBarType'
import { useStore } from 'vuex-composition-helpers'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import BatchEditForm from '@/components/batches/BatchEditForm.vue'
import { BatchPaymentTerms } from '@/models/batch'
import BatchEditFormFields from '@/components/batches/BatchEditFormFields.vue'
import BatchDraftForm from '@/components/batches/BatchDraftForm.vue'
import { assignWith } from 'lodash'

const props = defineProps<{
  jobUuid: string
  hubId?: string
}>()

const hubIdRef = computed(() => props.hubId)

const emits = defineEmits<{
  (e: 'job-added-success', batchId: string)
  (e: 'job-added-failure', message: unknown)
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const batchesListRef = ref()
const currentSearchQuery = ref('')
const selectedBatchId = ref<string | null | undefined>()
const isSnackbarVisible = ref(false)
const snackbarMessage = ref()
const showEditBatchForm = ref(false)
const showDraftForm = ref(false)
const draftBatchName = ref('')
const snackbarType = ref(SnackType.ERROR)

const store = useStore()

const {
  mutateAsync: addJobsToBatch,
  isLoading: addingJobsToBatch,
  error: addJobsToBatchError,
} = useAddJobsToInvoiceMutation()

const formTitle = computed<string>(() => {
  if (showDraftForm.value) return 'New Batch'
  if (showEditBatchForm.value) return 'Edit Batch'
  return 'Add to Batch'
})

const handleBatchCreatedSuccess = async (data: { batchId: string; batchNumber: string | null }) => {
  selectedBatchId.value = data.batchId

  if (showDraftForm.value) {
    showDraftForm.value = false
    showEditBatchForm.value = false
    emits('job-added-success', data.batchId)
    store.commit('jobs/SAVED_JOB')
    dismiss()
  } else {
    showEditBatchForm.value = true
    await save(false) // allow users to edit the newly created batch
  }
}

const handleBatchCreatedFailure = async (error: unknown) => {
  snackbarType.value = SnackType.ERROR
  isSnackbarVisible.value = true
  snackbarMessage.value = error
}

const dismiss = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isBatchBottomSheetVisible: false,
  })
}

const onSelect = async (newBatchId: string) => {
  selectedBatchId.value = newBatchId
  await save(true)
}

const handleEmptyBatchNumber = () => {
  handleBatchCreatedFailure('Batch number cannot be empty')
}

const handleBatchDraftRequested = (data: { batchName: string }) => {
  draftBatchName.value = data.batchName
  showDraftForm.value = true
}

async function save(dismissOnSave: boolean) {
  if (!selectedBatchId.value) {
    return
  }
  try {
    store.commit('jobs/SAVING_JOB')
    await addJobsToBatch({
      id: selectedBatchId.value,
      jobIds: [props.jobUuid],
    })
    isSnackbarVisible.value = false
    emits('job-added-success', selectedBatchId.value)
    store.commit('jobs/SAVED_JOB')
    if (dismissOnSave) {
      dismiss()
    }
  } catch (err) {
    snackbarType.value = SnackType.ERROR
    isSnackbarVisible.value = true
    snackbarMessage.value = addJobsToBatchError
    emits('job-added-failure', 'addToInvoiceError')
    store.commit('jobs/ERROR_SAVING_JOB')
  }
}
</script>

<style scoped></style>
